/**
 * Client for `/api/pipeline-definitions/*` (#885): the admin Pipelines page, the Lead Pipeline settings tab and
 * `hooks/usePipelineDefinition` (the one read every surface shares) all go through it.
 *
 * Reads of a pipeline and its versions are open to any org member; everything that writes, and
 * the template list, is admin-only and needs an MFA session. `force` is sent as `?force=true`: the
 * deal-stage guard (409 `deal_stage_in_use`) is lifted only by an explicit second request.
 */
import axios from 'axios';

// `|| ''`: an unset URL means same origin (`/api/...`), never `undefined/api/...`.
const BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api/pipeline-definitions`;

const forceQuery = (force) => (force ? '?force=true' : '');

export function createPipelineDefinitionsApi(getAuthHeader) {
    const cfg = (extra) => ({ headers: getAuthHeader ? getAuthHeader() : {}, ...extra });
    const id = (value) => encodeURIComponent(value);
    const data = (res) => res.data;
    return {
        get: (pipelineId) => axios.get(`${BASE}/${id(pipelineId)}`, cfg()).then(data),
        list: (recordKind) => axios.get(BASE, cfg({ params: { record_kind: recordKind } })).then(data),
        versions: (pipelineId, limit = 50) =>
            axios.get(`${BASE}/${id(pipelineId)}/versions`, cfg({ params: { limit } })).then(data),
        version: (pipelineId, version) => axios.get(`${BASE}/${id(pipelineId)}/versions/${version}`, cfg()).then(data),
        publish: (pipelineId, body, { force = false } = {}) =>
            axios.post(`${BASE}/${id(pipelineId)}/versions${forceQuery(force)}`, body, cfg()).then(data),
        restore: (pipelineId, version, expectedVersion, { force = false } = {}) =>
            axios.post(`${BASE}/${id(pipelineId)}/versions/${version}/restore${forceQuery(force)}`,
                       { expected_version: expectedVersion }, cfg()).then(data),
        rename: (pipelineId, body) => axios.put(`${BASE}/${id(pipelineId)}`, body, cfg()).then(data),
        clone: (pipelineId) => axios.post(`${BASE}/${id(pipelineId)}/clone`, null, cfg()).then(data),
        activate: (pipelineId) => axios.post(`${BASE}/${id(pipelineId)}/activate`, null, cfg()).then(data),
        deactivate: (pipelineId) => axios.post(`${BASE}/${id(pipelineId)}/deactivate`, null, cfg()).then(data),
        makeDefault: (pipelineId) => axios.post(`${BASE}/${id(pipelineId)}/make-default`, null, cfg()).then(data),
        templates: (recordKind) => axios.get(`${BASE}/templates`, cfg({ params: { record_kind: recordKind } })).then(data),
        saveAsTemplate: (body) => axios.post(`${BASE}/templates`, body, cfg()).then(data),
        updateTemplate: (templateId, body) => axios.put(`${BASE}/templates/${id(templateId)}`, body, cfg()).then(data),
        deleteTemplate: (templateId) => axios.delete(`${BASE}/templates/${id(templateId)}`, cfg()).then(data),
        applyTemplate: (templateId, body, { force = false } = {}) =>
            axios.post(`${BASE}/templates/${id(templateId)}/apply${forceQuery(force)}`, body, cfg()).then(data),
    };
}
